/**
 * `data-screen-label="Agent"`, tab `channels` — **Who may call this agent**, the access-list editor (plan-029
 * WAPP-ACCESS; design §40.7, §40.13.1, §40.13.2, §40.19.3, §40.25.2, §40.25.6; D-12 as changed at AP-0, DD29-21,
 * DD29-22; ADR 0081, proposed). WAPP-CONF (wave 7; design §40.7.1, §40.9.7, §40.19.3, §40.25.1; FR-IA-4, FR-IA-5) added
 * the assurance levels "number + text message code" and "number + passcode" and each person's passcode.
 *
 * ```
 * Who may call this agent                                                 [Not available yet]
 *   the internal warnings: "will soon answer only the people on its list…" / "refuses every caller until…" /
 *   "Choose who may call this agent before connecting it." and "Anyone who calls this number can ask about your data."
 *   (Open to anyone | Listed callers only)                                 [Save] [Discard]
 *   How callers are checked: By their phone number — "Caller numbers can be imitated…"
 *   People who may call   2 of 500
 *     Maria, front desk   +30 690 000 0001                                 [Rename] [Remove]
 *     Reception           201 · Your phone system                          [Rename] [Remove]
 *   Add a person: Name [……]  Calls from (A phone number | A number on your own phone system)  [+30 …]  [Add a person]
 *   Up to 500 people per agent.
 * ```
 *
 * THE SETTING LIVES ON THE VOICEAGENT, BESIDE THE VERSION (D-2): the switch saves with `If-Match` on the revision read
 * (`409 revision_stale` reloads it and says so) and applies from the next call. The people are rows of their own: adding,
 * renaming and removing apply at once, with no Save, so the list can be filled BEFORE it is switched on — the order the
 * owner's lock asks for (§40.25.2: "add them now"). Release 1 offered one assurance level, `number_only`, with its
 * spoofing warning. Release 2 (WAPP-CONF) offers "By their phone number and a text message code" while "Confirm who's
 * calling" says text message codes are available (switched on and priced — the price is shown, read from the api) and
 * "By their phone number and a passcode" while passcodes are; the level saves with the switch, under the same revision.
 *
 * A PASSCODE GOES ONE WAY (FR-IA-5): typed into a masked field, sent once, never shown again and never kept by the
 * portal after the save — a person on the list shows only "Passcode set · n digits" or "No passcode", with "Change
 * passcode" and "Remove passcode".
 *
 * THE LOCK (§40.25.2; CHANGED 2026-10-28 by plan-029 ACCESS-SPLIT). An internal agent (catalogue `audience: "internal"`)
 * with no list in force reads the warning of its state from the access read's `lock_state` (`./access-lock.tsx`):
 * pending before the lock, locked once it is on or whenever the list is on and empty. Once locked with its list off,
 * the binding refusal's sentence follows, since a number or the website chat can then be connected only once its list
 * is on (before the lock a binding goes ahead). Saving "Listed callers only" with no one on the list warns that every
 * caller is refused. While the api's switch is off (`available: false`) the stored values show and nothing can change.
 *
 * NUMBERS IN CLEAR ONLY TO `voice_agent.write`: the api sends an entry's full number to those members only; everyone
 * else reads "Number ending …123".
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@wetalk/api-client";
import { Button, Icon, Radio, Segmented, Tag } from "@wetalk/ui";
import { useState, type ReactNode } from "react";

import "../../new/answering.css";
import "./caller-protection-panel.css";
import "./access-panel.css";
import "./confirmation-panel.css";

import { apiErrorText } from "../../../../api/api-error.js";
import { useGateway } from "../../../../api/provider.js";
import { PartyRefusedError } from "../../../../api/slice/caller-protection.js";
import {
  PARTY_ACCESS_ENTRY_MOST,
  PARTY_ACCESS_PASSCODE_PATTERN,
  PARTY_ACCESS_QUERY_KEY,
  partyAccessLevelsOffered,
  useVoiceAgentAccess,
  useVoiceAgentAccessEntries,
  type PartyAccessAssuranceLevel,
  type PartyAccessChange,
  type PartyAccessEntryView,
  type PartyAccessIdentityInput,
  type PartyAccessView,
} from "../../../../api/slice/party-access.js";
import { useVoiceAgentPartyConfirmation, type PartyConfirmationSettingView } from "../../../../api/slice/party-confirmation.js";
import { portalText, usePortalTranslation } from "../../../../i18n/portal.js";
import { GLYPH } from "../../../../shell/glyph.js";
import { useMemberCan } from "../../../../shell/member-capability.js";
import { displayNumberOf } from "../../../numbers/number-display.js";
import { sipTrunkListQuery } from "../../../numbers/sip-trunk-query.js";
import { partyAccessLockOf, partyAccessLockTextOf } from "./access-lock.js";
import { confirmationCodePriceText } from "./confirmation-panel.js";

export interface AccessPanelProps {
  /** The VoiceAgent whose settings the panel shows. */
  readonly voiceAgentId: string;
  /** The VoiceAgent's agent_template (its audience and its declaration); `null` while unread. */
  readonly agentTemplateCode: string | null;
}

/** The example a typed number follows (a fictional Greek mobile range). */
export const ACCESS_NUMBER_EXAMPLE = "+30 690 000 0000";

/** A refused change in the portal's language, from its code and rule. */
export function partyAccessRefusalTextOf(error: unknown): string {
  if (error instanceof PartyRefusedError) {
    if (error.detailCode === "number_invalid") return portalText("callerProtection", "access.refused.number_invalid", { example: ACCESS_NUMBER_EXAMPLE });
    if (error.detailCode === "label_too_long") return portalText("callerProtection", "access.refused.label_too_long");
  }
  if (error instanceof ApiError) {
    switch (error.code) {
      case "access_entry_exists":
        return portalText("callerProtection", "access.refused.exists");
      case "access_entry_limit_reached":
        return portalText("callerProtection", "access.refused.limit");
      case "access_list_unavailable":
        return portalText("callerProtection", "access.refused.unavailable");
      case "assurance_level_unavailable":
        // plan-029 WAPP-CONF: Release 2 offers more levels, so the refusal no longer says "only numbers for now".
        return portalText("partyConfirmation", "access.refused.assurance");
      case "confirmation_code_price_unset":
        return portalText("partyConfirmation", "access.refused.price_unset");
      case "passcode_unavailable":
        return portalText("partyConfirmation", "passcode.unavailable");
      default:
        break;
    }
  }
  return apiErrorText(error, portalText("callerProtection", "access.refused.other"));
}

/** An entry's identity in words: the number in full for those who may see it, else its last three digits. */
export function partyAccessEntryNumberOf(entry: PartyAccessEntryView): string {
  if (entry.identityKind === "phone") {
    if (entry.phoneE164 !== null) return displayNumberOf(entry.phoneE164);
    return portalText("callerProtection", "access.entry_ending", { last_three: entry.lastThreeDigit ?? "" });
  }
  if (entry.identityKind === "trunk_local") return entry.trunkLocalNumber ?? "";
  return entry.signedVisitorSubject ?? "";
}

/**
 * The change a "Listed callers only" / "Open to anyone" choice saves: the level chosen, else the stored level, else
 * `number_only`.
 */
export function partyAccessChangeOf(
  stored: PartyAccessView,
  enabled: boolean,
  level: PartyAccessAssuranceLevel | null = null,
): PartyAccessChange {
  return { enabled, assuranceLevel: level ?? stored.assuranceLevel ?? "number_only", anySignedVisitor: stored.anySignedVisitor };
}

/** A level in the portal's words. */
export function partyAccessLevelText(level: PartyAccessAssuranceLevel): string {
  switch (level) {
    case "number_only":
      return portalText("callerProtection", "access.level_number_only");
    case "number_sms_code":
      return portalText("partyConfirmation", "access.level_number_sms_code");
    case "number_passcode":
      return portalText("partyConfirmation", "access.level_number_passcode");
    case "signed_visitor":
      return portalText("partyConfirmation", "method.signed_visitor");
  }
}

export function AccessPanel({ voiceAgentId }: AccessPanelProps): ReactNode {
  usePortalTranslation("callerProtection");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const canEdit = useMemberCan().can("voice_agent.write");
  const access = useVoiceAgentAccess(voiceAgentId);
  const entries = useVoiceAgentAccessEntries(voiceAgentId);
  const [edited, setEdited] = useState<boolean | null>(null);
  const [editedLevel, setEditedLevel] = useState<PartyAccessAssuranceLevel | null>(null);
  // plan-029 WAPP-CONF: which levels and passcodes can be offered, and the code price, from "Confirm who's calling".
  const confirmation = useVoiceAgentPartyConfirmation(voiceAgentId);
  const [savedLine, setSavedLine] = useState<"saved" | "reloaded" | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);

  const refresh = async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: PARTY_ACCESS_QUERY_KEY.access(voiceAgentId) }),
      queryClient.invalidateQueries({ queryKey: PARTY_ACCESS_QUERY_KEY.entry(voiceAgentId) }),
    ]);
  };

  const save = useMutation({
    mutationFn: (input: {
      readonly enabled: boolean;
      readonly level: PartyAccessAssuranceLevel | null;
      readonly stored: PartyAccessView;
    }) => gateway.changeVoiceAgentAccess(voiceAgentId, partyAccessChangeOf(input.stored, input.enabled, input.level), input.stored.revision),
    onSuccess: async (next) => {
      queryClient.setQueryData(PARTY_ACCESS_QUERY_KEY.access(voiceAgentId), next);
      setEdited(null);
      setEditedLevel(null);
      setRefusal(null);
      setSavedLine("saved");
    },
    onError: async (error) => {
      if (error instanceof ApiError && error.code === "revision_stale") {
        await queryClient.invalidateQueries({ queryKey: PARTY_ACCESS_QUERY_KEY.access(voiceAgentId) });
        setEdited(null);
        setEditedLevel(null);
        setRefusal(null);
        setSavedLine("reloaded");
        return;
      }
      setRefusal(partyAccessRefusalTextOf(error));
    },
  });

  const heading = <h2 className="ch-section-title">{portalText("callerProtection", "access.title")}</h2>;

  const failed = [access, entries].find((one) => one.isError);
  if (failed !== undefined) {
    return (
      <>
        {heading}
        <p className="c-note c-note-critical" role="alert">
          {portalText("callerProtection", "access.read_failed")}{" "}
          {apiErrorText(failed.error, portalText("callerProtection", "panel.reload"))}
        </p>
      </>
    );
  }
  if (access.data === undefined || entries.data === undefined) {
    return (
      <>
        {heading}
        <p className="c-note">{portalText("callerProtection", "access.reading")}</p>
      </>
    );
  }

  const stored = access.data;
  const enabled = edited ?? stored.enabled;
  const level = editedLevel ?? stored.assuranceLevel ?? "number_only";
  const changed = enabled !== stored.enabled || level !== (stored.assuranceLevel ?? "number_only");
  const confirmationSetting = confirmation.data ?? null;
  const levelOffered = partyAccessLevelsOffered(
    confirmationSetting === null || !confirmationSetting.available ? null : confirmationSetting.methodAvailability,
    stored.assuranceLevel,
  );
  const locked = !canEdit || !stored.available;
  const lock = partyAccessLockOf(stored);
  const people = entries.data.entry;
  const emptyWhileOn = enabled && people.length === 0 && !stored.anySignedVisitor;

  return (
    <>
      <div className="cp-head">
        {heading}
        {stored.available ? null : <Tag tone="neutral">{portalText("callerProtection", "panel.unavailable")}</Tag>}
      </div>
      <p className="ch-section-dek">{portalText("callerProtection", "access.dek")}</p>

      <div className="ch-card ap" data-party-access={stored.available ? "available" : "unavailable"}>
        {stored.available ? null : (
          <p className="ap-note" role="note">
            <Icon glyph={GLYPH.info} size="sm" />
            <span>{portalText("callerProtection", "access.unavailable_note")}</span>
          </p>
        )}

        {lock === null ? null : (
          <div className="ap-warn" role="status" data-access-lock={lock}>
            <Icon glyph={GLYPH.warning} size="lg" />
            <span>
              <strong>{partyAccessLockTextOf(lock)}</strong>
              {lock === "lock_pending" ? <> {portalText("callerProtection", "access.internal_warning")}</> : null}
              {lock === "locked" && !stored.enabled ? <> {portalText("callerProtection", "access.binding_refused")}</> : null}
            </span>
          </div>
        )}

        <div className="ap-row">
          {locked ? (
            <p className="ap-value">
              {stored.enabled ? portalText("callerProtection", "access.listed") : portalText("callerProtection", "access.open")}
            </p>
          ) : (
            <Segmented
              name={`ap-enabled-${voiceAgentId}`}
              legend={portalText("callerProtection", "access.title")}
              options={[
                { value: "open", label: portalText("callerProtection", "access.open") },
                { value: "listed", label: portalText("callerProtection", "access.listed") },
              ]}
              value={enabled ? "listed" : "open"}
              onChange={(value) => {
                setSavedLine(null);
                setRefusal(null);
                setEdited(value === "listed");
              }}
            />
          )}
          <span className="c-answer-note">
            {enabled ? portalText("callerProtection", "access.listed_dek") : portalText("callerProtection", "access.open_dek")}
          </span>
          {emptyWhileOn && changed ? (
            <span className="ap-empty-warn" role="note">
              {portalText("callerProtection", "access.empty_enabled_warning")}
            </span>
          ) : null}
        </div>

        {enabled ? (
          <AccessLevel
            voiceAgentId={voiceAgentId}
            level={level}
            offered={levelOffered}
            locked={locked}
            confirmation={confirmationSetting}
            withoutPasscode={people.filter((one) => !one.passcodeSet).length}
            onChange={(next) => {
              setSavedLine(null);
              setRefusal(null);
              setEditedLevel(next);
            }}
          />
        ) : null}

        {changed || savedLine !== null || refusal !== null ? (
          <div className="ch-card-actions">
            {changed ? (
              <Button
                variant="primary"
                disabled={save.isPending || locked}
                onClick={() => {
                  setRefusal(null);
                  save.mutate({ enabled, level, stored });
                }}
              >
                {save.isPending ? portalText("callerProtection", "panel.saving") : portalText("callerProtection", "panel.save")}
              </Button>
            ) : null}
            {changed ? (
              <Button
                variant="ghost"
                onClick={() => {
                  setEdited(null);
                  setEditedLevel(null);
                  setRefusal(null);
                }}
              >
                {portalText("callerProtection", "panel.discard")}
              </Button>
            ) : null}
            {refusal === null ? null : (
              <span className="c-note c-note-critical" role="alert">
                {refusal}
              </span>
            )}
            {savedLine === null ? null : (
              <span className="ch-note" role="status">
                {savedLine === "saved"
                  ? portalText("callerProtection", "panel.saved")
                  : portalText("callerProtection", "panel.revision_stale")}
              </span>
            )}
          </div>
        ) : null}

        <AccessEntries
          voiceAgentId={voiceAgentId}
          people={people}
          editable={canEdit && stored.available && entries.data.available}
          passcodeOffered={
            canEdit && stored.available && confirmationSetting?.available === true && confirmationSetting.methodAvailability.passcode
          }
          passcodeShown={level === "number_passcode" || people.some((one) => one.passcodeSet)}
          onChanged={refresh}
        />

        {canEdit ? null : <p className="ch-card-foot">{portalText("callerProtection", "panel.read_only")}</p>}
      </div>
    </>
  );
}

/** The people on the list, with rename and remove, and the add form. */
function AccessEntries({
  voiceAgentId,
  people,
  editable,
  passcodeOffered,
  passcodeShown,
  onChanged,
}: {
  readonly voiceAgentId: string;
  readonly people: readonly PartyAccessEntryView[];
  readonly editable: boolean;
  /** plan-029 WAPP-CONF: passcodes can be set and removed (the member may edit, and passcodes are available). */
  readonly passcodeOffered: boolean;
  /** plan-029 WAPP-CONF: each person's passcode state is shown (the level asks for one, or someone has one). */
  readonly passcodeShown: boolean;
  readonly onChanged: () => Promise<void>;
}): ReactNode {
  usePortalTranslation("callerProtection");
  const gateway = useGateway();
  const [line, setLine] = useState<{ readonly text: string; readonly refused: boolean } | null>(null);
  const [renaming, setRenaming] = useState<{ readonly partyAccessEntryId: string; readonly label: string } | null>(null);

  const rename = useMutation({
    mutationFn: (input: { readonly partyAccessEntryId: string; readonly label: string }) =>
      gateway.renameVoiceAgentAccessEntry(voiceAgentId, input.partyAccessEntryId, input.label.trim()),
    onSuccess: async (entry) => {
      setRenaming(null);
      setLine({ text: portalText("callerProtection", "access.entry_renamed", { label: entry.label }), refused: false });
      await onChanged();
    },
    onError: (error) => {
      setLine({ text: partyAccessRefusalTextOf(error), refused: true });
    },
  });

  const remove = useMutation({
    mutationFn: (entry: PartyAccessEntryView) => gateway.removeVoiceAgentAccessEntry(voiceAgentId, entry.partyAccessEntryId),
    onSuccess: async (_, entry) => {
      setLine({ text: portalText("callerProtection", "access.entry_removed", { label: entry.label }), refused: false });
      await onChanged();
    },
    onError: (error) => {
      setLine({ text: partyAccessRefusalTextOf(error), refused: true });
    },
  });

  return (
    <div className="ap-row">
      <div className="ap-entry-head">
        <p className="ch-card-label ch-card-label-flush">{portalText("callerProtection", "access.entry_title")}</p>
        <span className="ap-count">
          {portalText("callerProtection", "access.entry_count", { value: people.length, most: PARTY_ACCESS_ENTRY_MOST })}
        </span>
      </div>
      {people.length === 0 ? (
        <p className="ch-card-note">{portalText("callerProtection", "access.entry_empty")}</p>
      ) : (
        <ul className="ap-entries">
          {people.map((one) => (
            <li className="ap-entry" key={one.partyAccessEntryId} data-identity-kind={one.identityKind}>
              {renaming?.partyAccessEntryId === one.partyAccessEntryId ? (
                <form
                  className="ap-rename"
                  onSubmit={(event) => {
                    event.preventDefault();
                    if (renaming.label.trim() === "") return;
                    rename.mutate(renaming);
                  }}
                >
                  <div className="field">
                    <label htmlFor={`ap-rename-${one.partyAccessEntryId}`}>{portalText("callerProtection", "access.entry_rename_label")}</label>
                    <input
                      id={`ap-rename-${one.partyAccessEntryId}`}
                      className="input"
                      type="text"
                      maxLength={80}
                      value={renaming.label}
                      onChange={(event) => {
                        setRenaming({ partyAccessEntryId: one.partyAccessEntryId, label: event.currentTarget.value });
                      }}
                    />
                  </div>
                  <Button variant="primary" type="submit" disabled={rename.isPending || renaming.label.trim() === ""}>
                    {portalText("callerProtection", "access.entry_rename_save")}
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setRenaming(null);
                    }}
                  >
                    {portalText("callerProtection", "access.entry_cancel")}
                  </Button>
                </form>
              ) : (
                <>
                  <span className="ap-entry-body">
                    <span className="ap-entry-name">{one.label}</span>
                    <span className="ap-entry-number">
                      <span>{partyAccessEntryNumberOf(one)}</span>
                      {one.identityKind === "trunk_local" ? <Tag tone="neutral">{portalText("callerProtection", "access.entry_trunk_tag")}</Tag> : null}
                      {one.identityKind === "signed_visitor" ? (
                        <Tag tone="neutral">{portalText("callerProtection", "access.entry_visitor_tag")}</Tag>
                      ) : null}
                    </span>
                    {passcodeShown || passcodeOffered ? (
                      <AccessEntryPasscode
                        voiceAgentId={voiceAgentId}
                        entry={one}
                        editable={passcodeOffered}
                        onDone={async (text) => {
                          setLine({ text, refused: false });
                          await onChanged();
                        }}
                        onRefused={(text) => {
                          setLine({ text, refused: true });
                        }}
                      />
                    ) : null}
                  </span>
                  {editable ? (
                    <span className="ap-entry-actions">
                      <Button
                        variant="ghost"
                        aria-label={portalText("callerProtection", "access.entry_rename_named", { label: one.label })}
                        onClick={() => {
                          setLine(null);
                          setRenaming({ partyAccessEntryId: one.partyAccessEntryId, label: one.label });
                        }}
                      >
                        {portalText("callerProtection", "access.entry_rename")}
                      </Button>
                      <Button
                        variant="secondary"
                        disabled={remove.isPending}
                        aria-label={portalText("callerProtection", "access.entry_remove_named", { label: one.label })}
                        onClick={() => {
                          setLine(null);
                          remove.mutate(one);
                        }}
                      >
                        {portalText("callerProtection", "access.entry_remove")}
                      </Button>
                    </span>
                  ) : null}
                </>
              )}
            </li>
          ))}
        </ul>
      )}

      {editable && people.length < PARTY_ACCESS_ENTRY_MOST ? (
        <AccessEntryAdd
          voiceAgentId={voiceAgentId}
          onAdded={async (entry) => {
            setLine({ text: portalText("callerProtection", "access.entry_added", { label: entry.label }), refused: false });
            await onChanged();
          }}
          onRefused={(text) => {
            setLine({ text, refused: true });
          }}
          onEdit={() => {
            setLine(null);
          }}
        />
      ) : null}

      {line === null ? null : (
        <p className={line.refused ? "c-note c-note-critical" : "ch-note"} role={line.refused ? "alert" : "status"}>
          {line.text}
        </p>
      )}
      <p className="ch-card-foot">{portalText("callerProtection", "access.entry_limit")}</p>
    </div>
  );
}

interface AddDraft {
  readonly label: string;
  readonly kind: "phone" | "trunk_local";
  readonly number: string;
  readonly sipTrunkId: string;
  readonly trunkLocalNumber: string;
}

const EMPTY_DRAFT: AddDraft = { label: "", kind: "phone", number: "", sipTrunkId: "", trunkLocalNumber: "" };

/** "Add a person": a name and a phone number, or a number on one of the account's own SIP trunks. */
function AccessEntryAdd({
  voiceAgentId,
  onAdded,
  onRefused,
  onEdit,
}: {
  readonly voiceAgentId: string;
  readonly onAdded: (entry: PartyAccessEntryView) => Promise<void>;
  readonly onRefused: (text: string) => void;
  readonly onEdit: () => void;
}): ReactNode {
  usePortalTranslation("callerProtection");
  const gateway = useGateway();
  const trunk = useQuery(sipTrunkListQuery(gateway));
  const [draft, setDraft] = useState<AddDraft>(EMPTY_DRAFT);
  const trunkChoice = trunk.data ?? [];
  const chosenTrunk = draft.sipTrunkId !== "" ? draft.sipTrunkId : (trunkChoice[0]?.sipTrunkId ?? "");

  const add = useMutation({
    mutationFn: (identity: PartyAccessIdentityInput) => gateway.addVoiceAgentAccessEntry(voiceAgentId, { label: draft.label.trim(), identity }),
    onSuccess: async (entry) => {
      // The typed number leaves the form as soon as the api has it.
      setDraft({ ...EMPTY_DRAFT, kind: draft.kind, sipTrunkId: draft.sipTrunkId });
      await onAdded(entry);
    },
    onError: (error) => {
      onRefused(partyAccessRefusalTextOf(error));
    },
  });

  const change = (next: Partial<AddDraft>): void => {
    onEdit();
    setDraft({ ...draft, ...next });
  };

  const submit = (): void => {
    if (draft.label.trim() === "") {
      onRefused(portalText("callerProtection", "access.refused.label_missing"));
      return;
    }
    if (draft.kind === "phone") {
      add.mutate({ kind: "phone", number: draft.number.trim(), region: null });
      return;
    }
    if (chosenTrunk === "" || draft.trunkLocalNumber.trim() === "") {
      onRefused(portalText("callerProtection", "access.refused.trunk_missing"));
      return;
    }
    add.mutate({ kind: "trunk_local", sipTrunkId: chosenTrunk, trunkLocalNumber: draft.trunkLocalNumber.trim() });
  };

  const ready = draft.kind === "phone" ? draft.number.trim() !== "" : draft.trunkLocalNumber.trim() !== "";

  return (
    <form
      className="ap-add"
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      <p className="ch-card-label ch-card-label-flush">{portalText("callerProtection", "access.entry_add")}</p>
      <div className="ap-add-fields">
        <div className="field">
          <label htmlFor={`ap-add-label-${voiceAgentId}`}>{portalText("callerProtection", "access.entry_label")}</label>
          <input
            id={`ap-add-label-${voiceAgentId}`}
            className="input"
            type="text"
            maxLength={80}
            autoComplete="off"
            placeholder={portalText("callerProtection", "access.entry_label_hint")}
            value={draft.label}
            onChange={(event) => {
              change({ label: event.currentTarget.value });
            }}
          />
        </div>
        {trunkChoice.length === 0 ? null : (
          <div className="field">
            <span className="cp-field-label">{portalText("callerProtection", "access.entry_kind")}</span>
            <Segmented
              name={`ap-add-kind-${voiceAgentId}`}
              legend={portalText("callerProtection", "access.entry_kind")}
              options={[
                { value: "phone", label: portalText("callerProtection", "access.entry_kind_phone") },
                { value: "trunk_local", label: portalText("callerProtection", "access.entry_kind_trunk") },
              ]}
              value={draft.kind}
              onChange={(kind) => {
                change({ kind });
              }}
            />
          </div>
        )}
      </div>
      {draft.kind === "phone" ? (
        <div className="field">
          <label htmlFor={`ap-add-number-${voiceAgentId}`}>{portalText("callerProtection", "access.entry_number")}</label>
          <input
            id={`ap-add-number-${voiceAgentId}`}
            className="input"
            type="tel"
            inputMode="tel"
            autoComplete="off"
            placeholder={ACCESS_NUMBER_EXAMPLE}
            value={draft.number}
            onChange={(event) => {
              change({ number: event.currentTarget.value });
            }}
          />
          <span className="c-answer-note">{portalText("callerProtection", "access.entry_number_hint", { example: ACCESS_NUMBER_EXAMPLE })}</span>
        </div>
      ) : (
        <div className="ap-add-fields">
          <div className="field">
            <label htmlFor={`ap-add-trunk-${voiceAgentId}`}>{portalText("callerProtection", "access.entry_trunk")}</label>
            <span className="c-answer-select">
              <select
                id={`ap-add-trunk-${voiceAgentId}`}
                className="input"
                value={chosenTrunk}
                onChange={(event) => {
                  change({ sipTrunkId: event.currentTarget.value });
                }}
              >
                {trunkChoice.map((one) => (
                  <option key={one.sipTrunkId} value={one.sipTrunkId}>
                    {one.displayName}
                  </option>
                ))}
              </select>
              <Icon glyph={GLYPH.caretDown} size="sm" />
            </span>
          </div>
          <div className="field">
            <label htmlFor={`ap-add-trunk-number-${voiceAgentId}`}>{portalText("callerProtection", "access.entry_trunk_number")}</label>
            <input
              id={`ap-add-trunk-number-${voiceAgentId}`}
              className="input"
              type="text"
              maxLength={64}
              autoComplete="off"
              value={draft.trunkLocalNumber}
              onChange={(event) => {
                change({ trunkLocalNumber: event.currentTarget.value });
              }}
            />
            <span className="c-answer-note">{portalText("callerProtection", "access.entry_trunk_number_hint")}</span>
          </div>
        </div>
      )}
      <div className="ch-card-actions">
        <Button variant="primary" type="submit" disabled={add.isPending || !ready}>
          {add.isPending ? portalText("callerProtection", "access.entry_adding") : portalText("callerProtection", "access.entry_add")}
        </Button>
      </div>
    </form>
  );
}

/**
 * plan-029 WAPP-CONF: how the people on the list are checked — by their number, plus a text message code or their own
 * passcode where those are available. The text message code shows its price, read from the api, never a literal.
 */
function AccessLevel({
  voiceAgentId,
  level,
  offered,
  locked,
  confirmation,
  withoutPasscode,
  onChange,
}: {
  readonly voiceAgentId: string;
  readonly level: PartyAccessAssuranceLevel;
  readonly offered: readonly PartyAccessAssuranceLevel[];
  readonly locked: boolean;
  readonly confirmation: PartyConfirmationSettingView | null;
  readonly withoutPasscode: number;
  readonly onChange: (level: PartyAccessAssuranceLevel) => void;
}): ReactNode {
  usePortalTranslation("partyConfirmation");
  const note = (one: PartyAccessAssuranceLevel): ReactNode => {
    switch (one) {
      case "number_only":
        return <span className="c-answer-note">{portalText("callerProtection", "access.number_only_warning")}</span>;
      case "number_sms_code":
        return (
          <span className="c-answer-note">
            {portalText("partyConfirmation", "access.level_sms_code_note")}{" "}
            <span className="pc-price">{confirmationCodePriceText(confirmation?.confirmationCodePrice ?? null)}</span>
          </span>
        );
      case "number_passcode":
        return (
          <>
            <span className="c-answer-note">{portalText("partyConfirmation", "access.level_passcode_note")}</span>
            <span className="pc-warn" role="note">
              {portalText("partyConfirmation", "method.passcode_warning")}
            </span>
          </>
        );
      case "signed_visitor":
        return null;
    }
  };
  return (
    <div className="ap-row">
      <p className="ch-card-label ch-card-label-flush" id={`ap-level-${voiceAgentId}`}>
        {portalText("callerProtection", "access.level_label")}
      </p>
      {locked || offered.length === 1 ? (
        <>
          <p className="ap-value">{partyAccessLevelText(level)}</p>
          {note(level)}
          {offered.length === 1 && !locked ? (
            <span className="c-answer-note">{portalText("partyConfirmation", "access.level_unavailable")}</span>
          ) : null}
        </>
      ) : (
        <div className="pc-level" role="radiogroup" aria-labelledby={`ap-level-${voiceAgentId}`}>
          {offered.map((one) => (
            <div className="pc-method-option" key={one} data-level={one}>
              <Radio
                name={`ap-level-${voiceAgentId}`}
                value={one}
                label={partyAccessLevelText(one)}
                checked={level === one}
                onChange={() => {
                  onChange(one);
                }}
              />
              {level === one ? note(one) : null}
            </div>
          ))}
        </div>
      )}
      {level === "number_passcode" && withoutPasscode > 0 ? (
        <span className="pc-warn" role="note">
          {portalText("partyConfirmation", "passcode.missing_warning", { people: withoutPasscode })}
        </span>
      ) : null}
    </div>
  );
}

/**
 * plan-029 WAPP-CONF: one person's passcode — "Passcode set · n digits" or "No passcode", with "Set a passcode" /
 * "Change passcode" (a masked field, 4 to 8 digits) and "Remove passcode". The digits leave the form the moment the api
 * has them; the mutation is reset so they are not kept in its state either.
 */
function AccessEntryPasscode({
  voiceAgentId,
  entry,
  editable,
  onDone,
  onRefused,
}: {
  readonly voiceAgentId: string;
  readonly entry: PartyAccessEntryView;
  readonly editable: boolean;
  readonly onDone: (text: string) => Promise<void>;
  readonly onRefused: (text: string) => void;
}): ReactNode {
  usePortalTranslation("partyConfirmation");
  const gateway = useGateway();
  const [open, setOpen] = useState(false);
  const [digit, setDigit] = useState("");

  const set = useMutation({
    mutationFn: (passcode: string) => gateway.setVoiceAgentAccessEntryPasscode(voiceAgentId, entry.partyAccessEntryId, passcode),
    onSuccess: async () => {
      await onDone(portalText("partyConfirmation", "passcode.saved", { label: entry.label }));
    },
    onError: (error) => {
      onRefused(
        error instanceof ApiError && error.code === "validation_failed"
          ? portalText("partyConfirmation", "passcode.invalid")
          : partyAccessRefusalTextOf(error),
      );
    },
    onSettled: () => {
      setDigit("");
      setOpen(false);
    },
  });

  const remove = useMutation({
    mutationFn: () => gateway.removeVoiceAgentAccessEntryPasscode(voiceAgentId, entry.partyAccessEntryId),
    onSuccess: async () => {
      await onDone(portalText("partyConfirmation", "passcode.removed", { label: entry.label }));
    },
    onError: (error) => {
      onRefused(partyAccessRefusalTextOf(error));
    },
  });

  const fieldId = `ap-passcode-${entry.partyAccessEntryId}`;
  return (
    <span className="pc-passcode" data-passcode={entry.passcodeSet ? "set" : "none"}>
      <span className="pc-passcode-tag">
        {entry.passcodeSet
          ? portalText("partyConfirmation", "passcode.set_tag", { digits: entry.passcodeDigitCount ?? 0 })
          : portalText("partyConfirmation", "passcode.none")}
      </span>
      {editable && !open ? (
        <>
          <Button
            variant="ghost"
            aria-label={
              entry.passcodeSet
                ? portalText("partyConfirmation", "passcode.change_named", { label: entry.label })
                : portalText("partyConfirmation", "passcode.set_named", { label: entry.label })
            }
            onClick={() => {
              setOpen(true);
            }}
          >
            {entry.passcodeSet ? portalText("partyConfirmation", "passcode.change") : portalText("partyConfirmation", "passcode.set")}
          </Button>
          {entry.passcodeSet ? (
            <Button
              variant="ghost"
              disabled={remove.isPending}
              aria-label={portalText("partyConfirmation", "passcode.remove_named", { label: entry.label })}
              onClick={() => {
                remove.mutate();
              }}
            >
              {portalText("partyConfirmation", "passcode.remove")}
            </Button>
          ) : null}
        </>
      ) : null}
      {editable && open ? (
        <form
          className="pc-passcode"
          onSubmit={(event) => {
            event.preventDefault();
            if (!PARTY_ACCESS_PASSCODE_PATTERN.test(digit)) {
              onRefused(portalText("partyConfirmation", "passcode.invalid"));
              return;
            }
            // The digits leave the form at once; the mutation forgets them as soon as it settles.
            const passcode = digit;
            setDigit("");
            set.mutate(passcode, {
              onSettled: () => {
                set.reset();
              },
            });
          }}
        >
          <div className="field">
            <label htmlFor={fieldId}>{portalText("partyConfirmation", "passcode.label")}</label>
            <input
              id={fieldId}
              className="input"
              type="password"
              inputMode="numeric"
              autoComplete="off"
              maxLength={8}
              value={digit}
              onChange={(event) => {
                setDigit(event.currentTarget.value.replace(/[^0-9]/g, ""));
              }}
            />
            <span className="c-answer-note">{portalText("partyConfirmation", "passcode.hint")}</span>
          </div>
          <Button variant="primary" type="submit" disabled={set.isPending || digit.length < 4}>
            {set.isPending ? portalText("partyConfirmation", "passcode.saving") : portalText("partyConfirmation", "passcode.save")}
          </Button>
          <Button
            variant="ghost"
            onClick={() => {
              setDigit("");
              setOpen(false);
            }}
          >
            {portalText("partyConfirmation", "passcode.cancel")}
          </Button>
        </form>
      ) : null}
    </span>
  );
}
